import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Save, Key, ExternalLink, RefreshCw, Info, Youtube, Upload, Trash2, CheckCircle2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Fallback embedding models (used if API fetch fails)
const FALLBACK_EMBEDDING_MODELS = [
  { value: "text-embedding-3-small", label: "text-embedding-3-small (Recommended)", description: "Most cost-effective, 1536 dimensions" },
  { value: "text-embedding-3-large", label: "text-embedding-3-large", description: "Highest quality, 3072 dimensions" },
  { value: "text-embedding-ada-002", label: "text-embedding-ada-002 (Legacy)", description: "Older model, 1536 dimensions" },
];

export default function APIKeysSettings() {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    openrouter_api_key: "",
    openrouter_provisioning_key: "",
    default_model: "",
    openai_api_key: "",
    openai_embedding_model: "text-embedding-3-small",
    openai_admin_key: "",
    tensornex_api_key: "",
    firecrawl_api_key: "",
    reducto_api_key: "",
    cohere_api_key: "",
    whisper_model: "whisper-1",
  });

  // Dynamic model states
  const [openrouterModels, setOpenrouterModels] = useState([]);
  const [loadingModels, setLoadingModels] = useState(false);
  const [embeddingModels, setEmbeddingModels] = useState(FALLBACK_EMBEDDING_MODELS);
  const [loadingEmbedding, setLoadingEmbedding] = useState(false);
  const [tensornexTesting, setTensornexTesting] = useState(false);
  const [tensornexStatus, setTensornexStatus] = useState(null);
  const [providerTesting, setProviderTesting] = useState({});
  const [providerStatus, setProviderStatus] = useState({});
  const [whisperModels, setWhisperModels] = useState([]);

  // YouTube downloader cookies (for yt-dlp on bot-blocked IPs)
  const [youtubeCookies, setYoutubeCookies] = useState({ uploaded: false });
  const [cookiesUploading, setCookiesUploading] = useState(false);
  const [cookiesTesting, setCookiesTesting] = useState(false);
  const [cookiesTestResult, setCookiesTestResult] = useState(null);

  // Default LLM Models state (Phase F)
  const [defaultLlmModels, setDefaultLlmModels] = useState({});
  const [savingDefaults, setSavingDefaults] = useState(false);
  const [modelValidation, setModelValidation] = useState(null);
  const [validatingModels, setValidatingModels] = useState(false);

  useEffect(() => {
    fetchSettings();
    fetchOpenRouterModels();
    fetchEmbeddingModels();
    fetchDefaultLlmModels();
    fetchModelValidation();
    fetchWhisperModels();
    fetchYoutubeCookies();
  }, []);

  const fetchYoutubeCookies = async () => {
    try {
      const res = await fetch(`${API}/settings/youtube-cookies`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      if (res.ok) setYoutubeCookies(await res.json());
    } catch (e) { /* silent */ }
  };

  const uploadCookies = async (file) => {
    setCookiesUploading(true);
    setCookiesTestResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API}/settings/youtube-cookies`, {
        method: "POST",
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        body: fd,
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(`Cookies uploaded (${data.cookie_count} entries)`);
        fetchYoutubeCookies();
      } else {
        toast.error(data.detail || "Upload failed");
      }
    } catch (e) {
      toast.error("Upload failed");
    }
    setCookiesUploading(false);
  };

  const testCookies = async () => {
    setCookiesTesting(true);
    setCookiesTestResult(null);
    try {
      const res = await fetch(`${API}/settings/youtube-cookies/test`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      setCookiesTestResult(data);
      if (data.works) toast.success("YouTube download works!");
      else toast.error("YouTube download blocked");
    } catch (e) {
      toast.error("Test failed");
    }
    setCookiesTesting(false);
  };

  const removeCookies = async () => {
    try {
      const res = await fetch(`${API}/settings/youtube-cookies`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      if (res.ok) {
        toast.success("Cookies removed");
        setYoutubeCookies({ uploaded: false });
        setCookiesTestResult(null);
      }
    } catch (e) {
      toast.error("Remove failed");
    }
  };

  const fetchWhisperModels = async () => {
    try {
      const res = await fetch(`${API}/admin/whisper-models/fetch-from-openai`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      if (res.ok) {
        const data = await res.json();
        const models = data.models || data || [];
        setWhisperModels(models.map(m => typeof m === 'string' ? m : (m.id || m)));
      }
    } catch (e) { /* silent — will show fallback */ }
  };

  const fetchModelValidation = async () => {
    try {
      const res = await fetch(`${API}/settings/model-validation-status`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      if (res.ok) setModelValidation(await res.json());
    } catch (e) { /* silent */ }
  };

  const triggerModelValidation = async () => {
    setValidatingModels(true);
    try {
      const res = await fetch(`${API}/settings/model-validation-status/refresh`, {
        method: "POST",
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      if (res.ok) {
        toast.success("Model validation complete");
        fetchModelValidation();
      }
    } catch (e) { toast.error("Validation failed"); }
    setValidatingModels(false);
  };

  const fetchDefaultLlmModels = async () => {
    try {
      const res = await fetch(`${API}/settings/default-llm-models`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      if (res.ok) {
        const data = await res.json();
        setDefaultLlmModels(data);
      }
    } catch (e) {
      console.error("Failed to fetch default LLM models:", e);
    }
  };

  const saveDefaultLlmModels = async () => {
    setSavingDefaults(true);
    try {
      const res = await fetch(`${API}/settings/default-llm-models`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(defaultLlmModels),
      });
      if (res.ok) {
        toast.success("Default LLM models saved");
        const data = await res.json();
        setDefaultLlmModels(data.settings || defaultLlmModels);
      } else {
        toast.error("Failed to save default LLM models");
      }
    } catch (e) {
      toast.error("Error saving default LLM models");
    }
    setSavingDefaults(false);
  };

  const fetchOpenRouterModels = async () => {
    setLoadingModels(true);
    try {
      const response = await axios.get(`${API}/openrouter/models`);
      const models = response.data.models || [];
      
      // Group models by provider
      const groupedModels = models.map(model => {
        const id = model.id || "";
        let category = "Other";
        
        if (id.includes(":free")) category = "Free";
        else if (id.startsWith("openai/")) category = "OpenAI";
        else if (id.startsWith("anthropic/")) category = "Anthropic";
        else if (id.startsWith("google/")) category = "Google";
        else if (id.startsWith("deepseek/")) category = "DeepSeek";
        else if (id.startsWith("meta-llama/") || id.startsWith("meta/")) category = "Meta";
        else if (id.startsWith("mistralai/") || id.startsWith("mistral/")) category = "Mistral";
        else if (id.startsWith("qwen/")) category = "Qwen";
        
        return {
          value: id,
          label: model.name || id,
          category,
          description: model.description || "",
          context_length: model.context_length || 0,
        };
      });
      
      setOpenrouterModels(groupedModels);
    } catch (error) {
      console.error("Failed to fetch OpenRouter models:", error);
      toast.error("Failed to fetch models. Using cached list.");
    } finally {
      setLoadingModels(false);
    }
  };

  const fetchEmbeddingModels = async () => {
    setLoadingEmbedding(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/openai/embedding-models`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.models && response.data.models.length > 0) {
        setEmbeddingModels(response.data.models);
      }
    } catch (error) {
      console.error("Failed to fetch embedding models, using fallback:", error);
      // Keep using fallback models
    } finally {
      setLoadingEmbedding(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/settings/api-keys`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setFormData({
        openrouter_api_key: response.data.openrouter_api_key || "",
        openrouter_provisioning_key: response.data.openrouter_provisioning_key || "",
        default_model: response.data.default_model || "",
        openai_api_key: response.data.openai_api_key || "",
        openai_embedding_model: response.data.openai_embedding_model || "text-embedding-3-small",
        openai_admin_key: response.data.openai_admin_key || "",
        tensornex_api_key: response.data.tensornex_api_key || "",
        firecrawl_api_key: response.data.firecrawl_api_key || "",
        reducto_api_key: response.data.reducto_api_key || "",
        cohere_api_key: response.data.cohere_api_key || "",
        whisper_model: response.data.whisper_model || "whisper-1",
      });
    } catch (error) {
      toast.error("Failed to fetch API keys");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/settings/api-keys`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("API keys updated successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update API keys");
    } finally {
      setLoading(false);
    }
  };

  // Helper function to render model categories
  const renderModelsByCategory = (category) => {
    const filteredModels = openrouterModels.filter(m => m.category === category);
    if (filteredModels.length === 0) return null;
    
    return filteredModels.map((model) => (
      <SelectItem key={model.value} value={model.value}>
        <div className="flex items-center gap-2">
          <span className="truncate max-w-[300px]">{model.label}</span>
          {category === "Free" && (
            <Badge variant="secondary" className="text-xs bg-green-100 text-green-700">Free</Badge>
          )}
        </div>
      </SelectItem>
    ));
  };

  return (
    <div className="p-8" data-testid="api-keys-settings-page">
      <div className="max-w-3xl">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-800 mb-2">API Keys</h1>
          <p className="text-slate-600">Configure your OpenRouter API integration</p>
        </div>

        <Card className="border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="w-5 h-5" />
              OpenRouter Configuration
            </CardTitle>
            <CardDescription>
              Set up your OpenRouter API key to access various AI models.
              <a
                href="https://openrouter.ai/docs/quickstart"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 ml-2 text-indigo-600 hover:text-indigo-700 font-medium"
              >
                View Documentation <ExternalLink className="w-3 h-3" />
              </a>
            </CardDescription>
          </CardHeader>
          
          {/* Free Models Notice */}
          <div className="px-6 pb-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                  <span className="text-lg">🆓</span>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-green-900 mb-1">Free Models Available!</h4>
                  <p className="text-xs text-green-800 leading-relaxed">
                    Models marked as <span className="font-mono bg-green-100 px-1 rounded">(Free)</span> are <strong>completely free to use</strong> - no charges applied! 
                    You only need an OpenRouter API key (free to create). Paid models will consume credits from your OpenRouter account.
                  </p>
                  <a
                    href="https://openrouter.ai/models"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-green-700 hover:text-green-800 font-medium mt-2"
                  >
                    View pricing for all models <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>
            </div>
          </div>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="openrouter_api_key" className="text-slate-700 font-medium">
                  OpenRouter API Key (Required)
                </Label>
                <Input
                  id="openrouter_api_key"
                  type="password"
                  value={formData.openrouter_api_key}
                  onChange={(e) => setFormData({ ...formData, openrouter_api_key: e.target.value })}
                  placeholder="sk-or-v1-..."
                  className="h-11 border-slate-300 font-mono text-sm"
                  data-testid="openrouter-api-key-input"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Get your API key from{" "}
                  <a
                    href="https://openrouter.ai/keys"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-indigo-600 hover:underline"
                  >
                    OpenRouter Dashboard
                  </a>
                </p>
              </div>

              <div className="space-y-2 border-t border-slate-200 pt-6">
                <Label htmlFor="openrouter_provisioning_key" className="text-slate-700 font-medium">
                  Provisioning API Key (Optional - For Per-Model Stats)
                </Label>
                <Input
                  id="openrouter_provisioning_key"
                  type="password"
                  value={formData.openrouter_provisioning_key}
                  onChange={(e) => setFormData({ ...formData, openrouter_provisioning_key: e.target.value })}
                  placeholder="sk-or-v1-..."
                  className="h-11 border-slate-300 font-mono text-sm"
                  data-testid="openrouter-provisioning-key-input"
                />
                <div className="bg-blue-50 border border-blue-200 rounded p-3 mt-2">
                  <p className="text-xs text-blue-900 mb-2">
                    <strong>Why add a provisioning key?</strong>
                  </p>
                  <p className="text-xs text-blue-800 leading-relaxed">
                    A provisioning key allows you to see detailed <strong>per-model usage statistics</strong> in the Stats page. 
                    Without it, you&apos;ll only see account-level totals. Create one at{" "}
                    <a
                      href="https://openrouter.ai/settings/keys"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline font-medium"
                    >
                      OpenRouter Settings → API Keys → Create Provisioning Key
                    </a>
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="default_model" className="text-slate-700 font-medium">
                  Default Model
                </Label>
                <Select
                  value={formData.default_model}
                  onValueChange={(value) => setFormData({ ...formData, default_model: value })}
                >
                  <SelectTrigger className="h-11 border-slate-300" data-testid="default-model-select">
                    <SelectValue placeholder="Select a model" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[400px]">
                    {loadingModels ? (
                      <div className="flex items-center justify-center py-4">
                        <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                        <span className="text-sm text-slate-500">Loading models...</span>
                      </div>
                    ) : openrouterModels.length > 0 ? (
                      <>
                        {/* Free Models */}
                        {openrouterModels.some(m => m.category === "Free") && (
                          <>
                            <div className="px-2 py-1.5 text-xs font-semibold text-green-700 bg-green-50 sticky top-0">
                              🆓 Free Models
                            </div>
                            {renderModelsByCategory("Free")}
                          </>
                        )}
                        
                        {/* OpenAI Models */}
                        {openrouterModels.some(m => m.category === "OpenAI") && (
                          <>
                            <div className="px-2 py-1.5 text-xs font-semibold text-slate-700 bg-slate-50 mt-1">
                              OpenAI
                            </div>
                            {renderModelsByCategory("OpenAI")}
                          </>
                        )}
                        
                        {/* Anthropic Models */}
                        {openrouterModels.some(m => m.category === "Anthropic") && (
                          <>
                            <div className="px-2 py-1.5 text-xs font-semibold text-slate-700 bg-slate-50 mt-1">
                              Anthropic
                            </div>
                            {renderModelsByCategory("Anthropic")}
                          </>
                        )}
                        
                        {/* Google Models */}
                        {openrouterModels.some(m => m.category === "Google") && (
                          <>
                            <div className="px-2 py-1.5 text-xs font-semibold text-slate-700 bg-slate-50 mt-1">
                              Google
                            </div>
                            {renderModelsByCategory("Google")}
                          </>
                        )}
                        
                        {/* DeepSeek Models */}
                        {openrouterModels.some(m => m.category === "DeepSeek") && (
                          <>
                            <div className="px-2 py-1.5 text-xs font-semibold text-slate-700 bg-slate-50 mt-1">
                              DeepSeek
                            </div>
                            {renderModelsByCategory("DeepSeek")}
                          </>
                        )}
                        
                        {/* Meta Models */}
                        {openrouterModels.some(m => m.category === "Meta") && (
                          <>
                            <div className="px-2 py-1.5 text-xs font-semibold text-slate-700 bg-slate-50 mt-1">
                              Meta Llama
                            </div>
                            {renderModelsByCategory("Meta")}
                          </>
                        )}
                        
                        {/* Mistral Models */}
                        {openrouterModels.some(m => m.category === "Mistral") && (
                          <>
                            <div className="px-2 py-1.5 text-xs font-semibold text-slate-700 bg-slate-50 mt-1">
                              Mistral
                            </div>
                            {renderModelsByCategory("Mistral")}
                          </>
                        )}
                        
                        {/* Qwen Models */}
                        {openrouterModels.some(m => m.category === "Qwen") && (
                          <>
                            <div className="px-2 py-1.5 text-xs font-semibold text-slate-700 bg-slate-50 mt-1">
                              Qwen
                            </div>
                            {renderModelsByCategory("Qwen")}
                          </>
                        )}
                        
                        {/* Other Models */}
                        {openrouterModels.some(m => m.category === "Other") && (
                          <>
                            <div className="px-2 py-1.5 text-xs font-semibold text-slate-700 bg-slate-50 mt-1">
                              Other Providers
                            </div>
                            {renderModelsByCategory("Other")}
                          </>
                        )}
                      </>
                    ) : (
                      <div className="px-2 py-4 text-center text-sm text-slate-500">
                        No models available. Check your connection.
                      </div>
                    )}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">
                  Choose the default AI model for your applications. Free models are available at no cost!
                </p>
              </div>

              <div className="flex justify-end pt-4">
                <Button
                  type="submit"
                  disabled={loading}
                  className="bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 text-white font-semibold px-6"
                  data-testid="save-button"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {loading ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* OpenAI Embeddings Configuration */}
        <Card className="border-slate-200 shadow-sm mt-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-green-600" />
              OpenAI Embeddings (For Knowledge Base Search)
            </CardTitle>
            <CardDescription>
              Configure OpenAI API key for semantic search in Knowledge Base. Required for AI Training & Knowledge Base features.
              <a
                href="https://platform.openai.com/api-keys"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 ml-2 text-green-600 hover:text-green-700 font-medium"
              >
                Get API Key <ExternalLink className="w-3 h-3" />
              </a>
            </CardDescription>
          </CardHeader>
          
          {/* Info Notice */}
          <div className="px-6 pb-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                  <span className="text-lg">💡</span>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-yellow-900 mb-1">Why Do I Need This?</h4>
                  <p className="text-xs text-yellow-800 leading-relaxed">
                    OpenAI embeddings convert your Knowledge Base content into searchable vectors, enabling <strong>semantic search</strong>.
                    Without this, the AI Training &quot;Test&quot; feature and Knowledge Base search won&apos;t work.
                  </p>
                  <p className="text-xs text-yellow-800 leading-relaxed mt-2">
                    <strong>Cost:</strong> Very affordable - ~$0.00002 per 1k tokens. For 100 documents, typically less than $0.10.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="openai_api_key" className="text-slate-700 font-medium">
                  OpenAI API Key (Required for Embeddings)
                </Label>
                <Input
                  id="openai_api_key"
                  type="password"
                  value={formData.openai_api_key}
                  onChange={(e) => setFormData({ ...formData, openai_api_key: e.target.value })}
                  placeholder="sk-proj-..."
                  className="h-11 border-slate-300 font-mono text-sm"
                  data-testid="openai-api-key-input"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Get your API key from{" "}
                  <a
                    href="https://platform.openai.com/api-keys"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-green-600 hover:underline"
                  >
                    OpenAI Platform
                  </a>
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="openai_embedding_model" className="text-slate-700 font-medium">
                  Embedding Model
                </Label>
                <Select
                  value={formData.openai_embedding_model}
                  onValueChange={(value) => setFormData({ ...formData, openai_embedding_model: value })}
                >
                  <SelectTrigger className="h-11 border-slate-300" data-testid="embedding-model-select">
                    <SelectValue placeholder="Select embedding model" />
                  </SelectTrigger>
                  <SelectContent>
                    {loadingEmbedding ? (
                      <div className="flex items-center justify-center py-4">
                        <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                        <span className="text-sm text-slate-500">Loading models...</span>
                      </div>
                    ) : (
                      embeddingModels.map((model) => (
                        <SelectItem key={model.value} value={model.value}>
                          <div className="flex flex-col">
                            <span className="font-medium">{model.label}</span>
                            <span className="text-xs text-slate-500">{model.description}</span>
                          </div>
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">
                  <strong>Recommended:</strong> text-embedding-3-small for best cost/performance ratio
                </p>
              </div>

              <div className="flex justify-end pt-4">
                <Button
                  type="submit"
                  disabled={loading}
                  className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-semibold px-6"
                  data-testid="save-openai-button"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {loading ? "Saving..." : "Save OpenAI Settings"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* OpenAI Admin Key for Usage Stats */}
        <Card className="border-slate-200 shadow-sm mt-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-purple-600" />
              OpenAI Admin Key (For Usage Statistics)
            </CardTitle>
            <CardDescription>
              Configure an Admin API key to view detailed OpenAI usage statistics on the Stats page.
              <a
                href="https://platform.openai.com/settings/organization/admin-keys"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 ml-2 text-purple-600 hover:text-purple-700 font-medium"
              >
                Get Admin Key <ExternalLink className="w-3 h-3" />
              </a>
            </CardDescription>
          </CardHeader>
          
          {/* Info Notice */}
          <div className="px-6 pb-4">
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                  <span className="text-lg">📊</span>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-purple-900 mb-1">Why Do I Need This?</h4>
                  <p className="text-xs text-purple-800 leading-relaxed">
                    Admin API keys have special permissions to access the <strong>OpenAI Usage API</strong>, which provides detailed statistics on:
                  </p>
                  <ul className="text-xs text-purple-800 mt-2 space-y-1 ml-4 list-disc">
                    <li>Token usage by model (GPT-4, GPT-4o, etc.)</li>
                    <li>Embeddings usage (Knowledge Base)</li>
                    <li>Whisper transcription usage (voice features)</li>
                    <li>Daily costs and breakdowns by project</li>
                  </ul>
                  <p className="text-xs text-purple-800 leading-relaxed mt-2">
                    <strong>Note:</strong> Admin keys are different from regular API keys. Create one with &quot;Usage Read&quot; permissions.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="openai_admin_key" className="text-slate-700 font-medium">
                  OpenAI Admin API Key (For Usage Statistics)
                </Label>
                <Input
                  id="openai_admin_key"
                  type="password"
                  value={formData.openai_admin_key}
                  onChange={(e) => setFormData({ ...formData, openai_admin_key: e.target.value })}
                  placeholder="sk-admin-..."
                  className="h-11 border-slate-300 font-mono text-sm"
                  data-testid="openai-admin-key-input"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Create an Admin key at{" "}
                  <a
                    href="https://platform.openai.com/settings/organization/admin-keys"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-purple-600 hover:underline"
                  >
                    OpenAI Settings → Admin Keys
                  </a>
                  {" "}with &quot;Usage Read&quot; permission.
                </p>
              </div>

              <div className="flex justify-end pt-4">
                <Button
                  type="submit"
                  disabled={loading}
                  className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-semibold px-6"
                  data-testid="save-admin-key-button"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {loading ? "Saving..." : "Save Admin Key"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Tensornex GPU Platform */}
        <Card className="border-slate-200 shadow-sm mt-6" data-testid="tensornex-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-cyan-600" />
              Tensornex GPU Platform
              <Badge variant="secondary" className="text-xs bg-cyan-100 text-cyan-700">RAG Infrastructure</Badge>
            </CardTitle>
            <CardDescription>
              Powers RAG V4 infrastructure: embeddings (BGE-M3), reranking, vector search (Qdrant), keyword search (Elasticsearch), graph (Neo4j), cache (Redis), streaming (Kafka), and storage (MinIO) — all through one API key.
              <a
                href="https://tensornex.cloud"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 ml-2 text-cyan-600 hover:text-cyan-700 font-medium"
              >
                Tensornex Dashboard <ExternalLink className="w-3 h-3" />
              </a>
            </CardDescription>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="tensornex_api_key" className="text-slate-700 font-medium">
                  Tensornex API Key
                </Label>
                <div className="flex gap-2">
                  <Input
                    id="tensornex_api_key"
                    type="password"
                    value={formData.tensornex_api_key}
                    onChange={(e) => setFormData({ ...formData, tensornex_api_key: e.target.value })}
                    placeholder="gs_live_..."
                    className="h-11 border-slate-300 font-mono text-sm flex-1"
                    data-testid="tensornex-api-key-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    disabled={tensornexTesting || !formData.tensornex_api_key}
                    data-testid="tensornex-test-btn"
                    onClick={async () => {
                      setTensornexTesting(true);
                      setTensornexStatus(null);
                      try {
                        const res = await fetch(`${API}/settings/test-provider-connection`, {
                          method: "POST",
                          headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` },
                          body: JSON.stringify({ provider: "tensornex", api_key: formData.tensornex_api_key }),
                        });
                        const data = await res.json();
                        const results = data.services || {};
                        setTensornexStatus(Object.keys(results).length ? results : null);
                        if (data.connected && data.message && Object.values(results).every(v => v)) {
                          toast.success(data.message);
                        } else if (data.connected) {
                          toast.warning(data.message || "Some services unreachable");
                        } else {
                          toast.error(data.error || "Connection test failed");
                        }
                      } catch (e) {
                        toast.error("Connection test failed: " + e.message);
                        setTensornexStatus(null);
                      } finally {
                        setTensornexTesting(false);
                      }
                    }}
                  >
                    {tensornexTesting ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <Key className="w-4 h-4 mr-1" />}
                    {tensornexTesting ? "Testing..." : "Test Connection"}
                  </Button>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Your API key starts with <code className="bg-slate-100 px-1 rounded">gs_live_</code>. Get it from your{" "}
                  <a href="https://tensornex.cloud" target="_blank" rel="noopener noreferrer" className="text-cyan-600 hover:underline">
                    Tensornex dashboard
                  </a>.
                </p>
              </div>

              {/* Service Status Grid */}
              {tensornexStatus && (
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="tensornex-service-grid">
                  {Object.entries(tensornexStatus).map(([name, ok]) => (
                    <div key={name} className={`flex items-center gap-1.5 px-2 py-1.5 rounded text-xs font-medium ${ok ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
                      <span className={`w-2 h-2 rounded-full ${ok ? 'bg-green-500' : 'bg-red-500'}`}></span>
                      {name}
                    </div>
                  ))}
                </div>
              )}

              <div className="flex justify-end pt-4">
                <Button
                  type="submit"
                  disabled={loading}
                  className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white font-semibold px-6"
                  data-testid="save-tensornex-key-button"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {loading ? "Saving..." : "Save Tensornex Key"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* YouTube Downloader Cookies */}
        <Card className="border-slate-200 shadow-sm mt-6" data-testid="youtube-cookies-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Youtube className="w-5 h-5 text-red-600" /> YouTube Downloader Cookies
            </CardTitle>
            <CardDescription>
              Upload a <code className="bg-slate-100 px-1 rounded">cookies.txt</code> exported from a
              logged-in YouTube session. This lets the video pipeline download &amp; transcribe YouTube
              videos even when the server IP is bot-blocked. Stored encrypted.{" "}
              <a
                href="https://github.com/yt-dlp/yt-dlp/wiki/FAQ#how-do-i-pass-cookies-to-yt-dlp"
                target="_blank"
                rel="noopener noreferrer"
                className="text-cyan-600 hover:underline"
              >
                How to export cookies
              </a>.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div
              className="flex items-center justify-between border border-slate-200 rounded-lg p-3"
              data-testid="youtube-cookies-status"
            >
              <div className="text-sm">
                {youtubeCookies?.uploaded ? (
                  <span className="text-green-700 font-medium flex items-center gap-1">
                    <CheckCircle2 className="w-4 h-4" /> Cookies uploaded
                    <span className="text-slate-500 font-normal">
                      — {youtubeCookies.cookie_count} entries
                      {youtubeCookies.uploaded_at ? `, ${new Date(youtubeCookies.uploaded_at).toLocaleString()}` : ""}
                    </span>
                  </span>
                ) : (
                  <span className="text-slate-500">No cookies uploaded yet.</span>
                )}
              </div>
              {youtubeCookies?.uploaded && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-red-600 hover:text-red-700"
                  disabled={cookiesUploading}
                  data-testid="youtube-cookies-remove-btn"
                  onClick={removeCookies}
                >
                  <Trash2 className="w-4 h-4 mr-1" /> Remove
                </Button>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <label className="inline-flex">
                <input
                  type="file"
                  accept=".txt"
                  className="hidden"
                  data-testid="youtube-cookies-file-input"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) uploadCookies(f);
                    e.target.value = "";
                  }}
                />
                <span
                  className="cursor-pointer inline-flex items-center px-4 py-2 rounded-md bg-slate-800 text-white text-sm font-medium hover:bg-slate-900 transition-colors"
                  data-testid="youtube-cookies-upload-btn"
                >
                  {cookiesUploading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                  {cookiesUploading ? "Uploading..." : "Upload cookies.txt"}
                </span>
              </label>
              <Button
                type="button"
                variant="outline"
                disabled={!youtubeCookies?.uploaded || cookiesTesting}
                data-testid="youtube-cookies-test-btn"
                onClick={testCookies}
              >
                {cookiesTesting ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <Youtube className="w-4 h-4 mr-1" />}
                {cookiesTesting ? "Testing..." : "Test YouTube"}
              </Button>
            </div>

            {cookiesTestResult && (
              <div
                className={`px-3 py-2 rounded-md text-sm font-medium ${cookiesTestResult.works ? "bg-green-50 border border-green-200 text-green-700" : "bg-red-50 border border-red-200 text-red-700"}`}
                data-testid="youtube-cookies-test-result"
              >
                {cookiesTestResult.works
                  ? `✓ YouTube download works (${cookiesTestResult.downloaded_kb} KB, ${cookiesTestResult.format}${cookiesTestResult.cookies_used ? ", cookies used" : ""})`
                  : `✗ ${cookiesTestResult.error}`}
              </div>
            )}
          </CardContent>
        </Card>

        {/* RAG Processing Providers — Phase A */}
        <Card className="border-slate-200 shadow-sm" data-testid="rag-providers-card">
          <CardHeader>
            <CardTitle>RAG Processing Providers</CardTitle>
            <CardDescription>
              API keys for document processing, web scraping, embeddings, and transcription providers.
              These are used by the CDM Documents pipeline when configured as primary providers.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {[
              { key: "firecrawl_api_key", name: "Firecrawl", provider: "firecrawl", desc: "URL and website scraping", prefix: "fc-", docsUrl: "https://docs.firecrawl.dev" },
              { key: "reducto_api_key", name: "Reducto", provider: "reducto", desc: "Document parsing (PDF, DOCX, images)", prefix: "", docsUrl: "https://docs.reducto.ai" },
              { key: "cohere_api_key", name: "Cohere", provider: "cohere", desc: "Embeddings and reranking", prefix: "", docsUrl: "https://docs.cohere.com" },
            ].map(({ key, name, provider, desc, prefix, docsUrl }) => (
              <div key={key} className="border border-slate-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <h4 className="font-medium text-slate-800">{name}</h4>
                    <p className="text-xs text-slate-500">{desc}</p>
                  </div>
                  <a href={docsUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-cyan-600 hover:underline flex items-center gap-1">
                    Docs <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="password"
                    className="flex-1 px-3 py-2 border border-slate-300 rounded-md text-sm"
                    placeholder={prefix ? `${prefix}...` : "Enter API key"}
                    value={formData[key] || ""}
                    onChange={(e) => setFormData(prev => ({ ...prev, [key]: e.target.value }))}
                    data-testid={`input-${key}`}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={!formData[key] || providerTesting[provider]}
                    data-testid={`test-${provider}-btn`}
                    onClick={async () => {
                      setProviderTesting(prev => ({ ...prev, [provider]: true }));
                      setProviderStatus(prev => ({ ...prev, [provider]: null }));
                      try {
                        const res = await fetch(`${API}/settings/test-provider-connection`, {
                          method: "POST",
                          headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` },
                          body: JSON.stringify({ provider, api_key: formData[key] }),
                        });
                        const data = await res.json();
                        setProviderStatus(prev => ({ ...prev, [provider]: data }));
                        if (data.connected) toast.success(`${name}: ${data.message}`);
                        else toast.error(`${name}: ${data.error}`);
                      } catch (e) {
                        toast.error(`${name} test failed`);
                      }
                      setProviderTesting(prev => ({ ...prev, [provider]: false }));
                    }}
                  >
                    {providerTesting[provider] ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : "Test"}
                  </Button>
                </div>
                {providerStatus[provider] && (
                  <div className={`mt-2 px-3 py-2 rounded-md text-sm font-medium ${providerStatus[provider].connected ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'}`}>
                    {providerStatus[provider].connected
                      ? `✓ Connected — ${providerStatus[provider].message || 'API key is valid'}`
                      : `✗ Failed — ${providerStatus[provider].error}`}
                  </div>
                )}
              </div>
            ))}
            <div className="border border-slate-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-1">
                <div>
                  <h4 className="font-medium text-slate-800">OpenAI Whisper</h4>
                  <p className="text-xs text-slate-500">Audio and video transcription — uses your OpenAI API key configured above</p>
                </div>
              </div>
              <p className="text-xs text-slate-400 mb-2">No separate key needed. Whisper uses the OpenAI API key from the section above.</p>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Whisper Model</label>
                <select
                  className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white"
                  data-testid="whisper-model-select"
                  value={formData.whisper_model || "whisper-1"}
                  onChange={(e) => setFormData(prev => ({ ...prev, whisper_model: e.target.value }))}
                >
                  {whisperModels.length > 0 ? (
                    whisperModels.map((m) => (
                      <option key={m} value={m}>{m}</option>
                    ))
                  ) : (
                    <option value="whisper-1">whisper-1</option>
                  )}
                </select>
                <p className="text-xs text-slate-400 mt-1">
                  {whisperModels.length > 0
                    ? `${whisperModels.length} models available from OpenAI`
                    : "Models will load when OpenAI API key is configured"}
                </p>
              </div>
            </div>
            <div className="flex justify-end mt-4">
              <Button
                type="button"
                onClick={handleSubmit}
                disabled={loading}
                data-testid="save-provider-keys-btn"
              >
                <Save className="w-4 h-4 mr-1" />
                {loading ? "Saving..." : "Save Provider Keys"}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Default LLM Models — Phase F */}
        <Card className="border-slate-200 shadow-sm" data-testid="default-llm-models-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              Default LLM Models
            </CardTitle>
            <CardDescription>
              Configure default models used when no specific model is set for an AI actor or feature.
              All models are fetched live from the OpenRouter API. Requires OpenRouter API key (configured above) to execute.
            </CardDescription>
            <div className="flex items-center justify-between mt-2">
              <div className="text-xs text-slate-500">
                {modelValidation?.validated_at
                  ? `Last validated: ${new Date(modelValidation.validated_at).toLocaleString()}`
                  : "Not validated yet"}
                {modelValidation?.invalid_count > 0 && (
                  <span className="ml-2 text-orange-500 font-medium">
                    {modelValidation.invalid_count} model(s) no longer available
                  </span>
                )}
              </div>
              <button
                onClick={triggerModelValidation}
                disabled={validatingModels}
                className="text-xs px-2 py-1 bg-slate-100 hover:bg-slate-200 rounded border text-slate-600"
                data-testid="check-models-btn"
              >
                {validatingModels ? "Checking..." : "Check Now"}
              </button>
            </div>
            {modelValidation?.invalid_count > 0 && (
              <div className="mt-2 p-2 bg-orange-50 border border-orange-200 rounded text-xs text-orange-700" data-testid="invalid-models-banner">
                <strong>Warning:</strong> {modelValidation.invalid_count} configured model(s) are no longer available on OpenRouter.
                Please update them to avoid failures.
                {modelValidation.invalid_models?.map(m => (
                  <div key={m.model_id} className="mt-1 ml-2">
                    <code className="bg-orange-100 px-1 rounded">{m.model_id}</code>
                    <span className="text-orange-500 ml-1">— used by: {m.used_by?.join(", ")}</span>
                  </div>
                ))}
              </div>
            )}
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { key: "chat_model", label: "Chat (fallback)", tooltip: "Fallback model for all chat conversations. Used when an AI Actor has no specific model configured. Affects: Public Chat Widget, Private Chat, WhatsApp AI replies, attachment analysis. Pages: AI Actors → Chat." },
                { key: "guard_rail_model", label: "Guard Rails", tooltip: "Content safety gate before chat responses. Checks if user messages are appropriate before the main LLM processes them. Affects: Public Chat Widget, WhatsApp chat. Runs silently before every response." },
                { key: "new_actor_model", label: "New Actor Template", tooltip: "Pre-filled model when creating a new AI Actor. Only sets the initial value — can be changed per actor after creation. Affects: AI Actors → Create New Actor form." },
                { key: "backup_model", label: "Backup / Fallback", tooltip: "Last-resort model in the fallback chain. If an actor's primary and secondary models both fail, this model is tried before returning an error. Affects: All chat flows (Public, Private, WhatsApp)." },
                { key: "email_model", label: "Email AI (Draft/Reply)", tooltip: "Model for drafting and replying to emails. Used when composing new emails, generating replies, and auto-categorizing emails. Affects: Email Integration → Compose, Reply, Auto-categorize. Pages: Settings → Communication → Email." },
                { key: "email_analysis_model", label: "Email Analysis", tooltip: "Model for analyzing email writing style and content. Used for writing style analysis, email thread summarization, and advanced categorization. Affects: Email Integration → Analysis features. Pages: Settings → Communication → Email." },
                { key: "security_scan_model", label: "Security Scan", tooltip: "Model for scanning uploaded file content. Checks files uploaded via chat for potentially unsafe or malicious content before processing. Affects: Chat Widget → File Upload. Runs silently in background." },
                { key: "kb_correction_model", label: "KB Correction", tooltip: "Model for AI-powered PDF text correction. When uploading PDFs to the knowledge base with correction enabled, this model fixes OCR errors and formatting issues. Affects: CDM Documents → Upload PDF (with AI correction toggle)." },
                { key: "relevance_check_model", label: "Relevance Check", tooltip: "Model for checking question relevance. Determines whether a user's question is relevant to the knowledge base before searching. Affects: Public Chat → KB search gate. Runs silently before retrieval." },
                { key: "language_detect_model", label: "Language Detection", tooltip: "Model for detecting document language. When a document is imported, this model identifies the language (Spanish, English, etc.) for proper summary generation and transcription hints. Affects: CDM Documents → All imports (URL, PDF, Zoho). Pages: CDM Documents → Language Detection Settings." },
                { key: "thinking_fallback_model", label: "Thinking Layer Fallback", tooltip: "Fallback model for intent detection. When the primary Thinking Layer model fails, this model classifies user intent (greeting, booking, general query, etc.). Affects: All chat flows. Pages: AI Actors → Thinking Layer." },
                { key: "rag_config_model", label: "RAG Config Model", tooltip: "Model reference in RAG retrieval configuration. Used as the embedding model reference in the RAG pipeline config. Affects: CDM Documents → Search/Retrieval configuration. Pages: Settings → RAG Config." },
              ].map(({ key, label, tooltip }) => {
                const currentValue = defaultLlmModels[key] || "";
                const isInvalid = modelValidation?.invalid_models?.some(m => m.model_id === currentValue);
                return (
                <div key={key}>
                  <label className="block text-sm font-medium text-slate-700 mb-1 flex items-center gap-1">
                    {label}
                    <span className="relative group cursor-help">
                      <Info className="w-3.5 h-3.5 text-slate-400 hover:text-slate-600" />
                      <span className="absolute hidden group-hover:block z-50 w-72 p-2.5 text-xs leading-relaxed bg-slate-800 text-white rounded-lg shadow-lg -left-2 bottom-full mb-1.5 pointer-events-none">
                        {tooltip}
                        <span className="absolute left-3 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-slate-800"></span>
                      </span>
                    </span>
                    {isInvalid && (
                      <span className="text-orange-500 text-xs ml-1" title="This model is no longer available on OpenRouter">
                        (unavailable)
                      </span>
                    )}
                  </label>
                  <select
                    className={`w-full px-3 py-2 border rounded-md text-sm bg-white ${isInvalid ? 'border-orange-400 bg-orange-50' : 'border-slate-300'}`}
                    data-testid={`default-llm-${key}`}
                    value={currentValue}
                    onChange={(e) => setDefaultLlmModels(prev => ({ ...prev, [key]: e.target.value }))}
                  >
                    <option value="">Select model...</option>
                    {openrouterModels.map((m) => (
                      <option key={m.value} value={m.value}>{m.label || m.value}</option>
                    ))}
                    {currentValue && !openrouterModels.some(m => m.value === currentValue) && (
                      <option value={currentValue}>{currentValue} (not found on OpenRouter)</option>
                    )}
                  </select>
                </div>
                );
              })}
            </div>
            <div className="mt-4 flex justify-end">
              <Button
                onClick={saveDefaultLlmModels}
                disabled={savingDefaults}
                data-testid="save-default-llm-btn"
              >
                {savingDefaults ? "Saving..." : "Save Default Models"}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}